import * as Phaser from "phaser";
import {generateAllSpritesAnimations} from "./animations"
import {Joinable, Synchroniser} from "./synchroniser";
import {MoveCommand} from "../model/messages";
import {mouseDirectionFromSprite} from "./inputs";
import {CookSprite} from "./cook_sprite";
import {Ingredient, IngredientTypes} from "../model/ingredients";
import {StationType, stationOutputType} from "../model/station";
import {RECIPES} from "../model/recipe_registry";
import {IngredientSprite} from "./ingredient_sprite";
import {Cook, CookSpriteNames} from "../model/cooks";
import {Client} from "colyseus.js";
import path from "path";
import {mainLogger} from "./client_logger";
import {BasketSprite} from "./basket_sprite";
import {Basket} from "../model/basket";
import {StationSprite} from "./station_sprite";
import {Station} from "../model/station";
import {GameSpriteGroup, IGameSprite} from "./game_sprite_base";
import CursorKeys = Phaser.Types.Input.Keyboard.CursorKeys;
import Config = Phaser.Core.Config;

import DOWN = Phaser.Input.Keyboard.Events.DOWN;
import EventEmitter = Phaser.Events.EventEmitter;
import GameObject = Phaser.GameObjects.GameObject;
import {Position} from "../model/game_element_schema";
import {withinReach} from "../model/action_utils";
import {getAllDishTypes} from "../model/recipe_registry";
import {OrdersHUD} from "./orders_hud";
import {CANVAS_WIDTH, CANVAS_HEIGHT, CANVAS_HEIGHT_TOUCH, MAP_WIDTH, MAP_HEIGHT, HUD_HEIGHT} from "./config";
import {isTouchDevice, TouchInputHandler} from "./touch_input";
import {MobileControls} from "./mobile_controls";

const DISH_TYPES = new Set(getAllDishTypes());
import UP = Phaser.Input.Keyboard.Events.UP;

export const logger = mainLogger.child({label: 'client.scene'})

export const SERVER_ENDPOINT = window?.location.toString().replace('http', 'ws');

export const KITCHEN_SCENE_KEY = 'kitchen';

export enum KitchenAssetNames {
    kitchen_floor = "kitchen_floor",
    kitchen_map = "kitchen_map",
    ingredients = "ingredients",
    elements = "elements",
    tomato = "tomato",
    salad = "salad",
    tomato_salad = "tomato_salad",
    station_cutting_board = "station_cutting_board",
    station_stove = "station_stove"
}

export class KitchenScene extends Phaser.Scene {
    sessionId: string
    cursors: CursorKeys

    floor: Phaser.Tilemaps.TilemapLayer
    furnitures: Phaser.Tilemaps.TilemapLayer

    restingIngredientsLayer: Phaser.GameObjects.Layer
    cookLayer: Phaser.GameObjects.Layer
    objectsLayer: Phaser.GameObjects.Layer;
    stationOverlayLayer: Phaser.GameObjects.Layer;

    private hudCamera: Phaser.Cameras.Scene2D.Camera;

    ingredientObjects: GameSpriteGroup<Ingredient, IngredientSprite>
    cookObjects: GameSpriteGroup<Cook, CookSprite>
    basketObjects: GameSpriteGroup<Basket, BasketSprite>
    stationObjects: GameSpriteGroup<Station, StationSprite>;

    localCookSprite: CookSprite
    declare events: EventEmitter
    synchroniser: Synchroniser

    private ordersHUD: OrdersHUD
    private hintText: Phaser.GameObjects.Text
    private waitingOverlay: Phaser.GameObjects.Container
    private isChopping: boolean = false
    private lobbyRoomId: string | null = null
    private lobbyPlayerName: string | null = null
    private recipeBookVisible: boolean = false
    private recipeBookPanel: Phaser.GameObjects.Container | null = null

    private _wasRoundActive: boolean = false
    private _postRoundOverlay: Phaser.GameObjects.Container | null = null
    private _touchInput: TouchInputHandler | null = null
    private _mobileControls: MobileControls | null = null
    private _waitingOverlayPlayerCount: number = -1

    constructor(config?: Config) {
        super({...(config || {}), key: KITCHEN_SCENE_KEY} as any);
        this.events = new EventEmitter()
        this.synchroniser = new Synchroniser()
    }

    init(data?: {roomId?: string, playerName?: string}) {
        if (data?.roomId) {
            this.lobbyRoomId = data.roomId;
        }
        if (data?.playerName) {
            this.lobbyPlayerName = data.playerName;
        }
    }

    preload() {
        this.load.spritesheet(KitchenAssetNames.kitchen_floor, path.join(__dirname, '../../' +
            'assets/sprites/Tiles.png'), {frameWidth: 32});
        IngredientSprite.preload(this)
        this.load.spritesheet(KitchenAssetNames.elements,
            path.join(__dirname, '../../assets/sprites/objects/TilesetElement.png'), {frameWidth: 16});
        for (const cook_name in CookSpriteNames) {
            this.load.spritesheet(cook_name,
                path.join(__dirname, `../../assets/sprites/${cook_name}/SpriteSheet.png`), {frameWidth: 16});
        }
        this.load.image(KitchenAssetNames.station_cutting_board, path.join(__dirname, '../../assets/sprites/stations/cutting_board.png'));
        this.load.image(KitchenAssetNames.station_stove, path.join(__dirname, '../../assets/sprites/stations/stove.png'));
        this.load.tilemapTiledJSON(KitchenAssetNames.kitchen_map, path.join(__dirname, '../../assets/maps/kitchen_1.json'))
    }

    create() {
        IngredientSprite.generateDynamicTextures(this);
        this.createDisplayLayers();
        this.setupCameras();

        generateAllSpritesAnimations(this.anims);
        this.prepare_input();
        this.createHUD();
        this.synchroniser.bringSceneToAServerRoom(this, this.colyseusClientFactory(), this.lobbyRoomId, this.lobbyPlayerName);
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) this.snapAllCooksToServer();
        });
    }

    private setupCameras(): void {
        // Main camera shows only the map area (below the HUD band)
        this.cameras.main.setViewport(0, HUD_HEIGHT, MAP_WIDTH, MAP_HEIGHT);

        // HUD camera covers the full canvas — includes controls zone on touch devices
        const hudHeight = isTouchDevice() ? CANVAS_HEIGHT_TOUCH : CANVAS_HEIGHT;
        this.hudCamera = this.cameras.add(0, 0, CANVAS_WIDTH, hudHeight);

        // Main camera ignores HUD / overlay layers — only game layers scroll with the map
        // (HUD objects are added to hudCamera's exclusive list via ignorance of main camera)
        // HUD camera ignores game layers so it only renders HUD elements
        // TilemapLayers are ignored individually (they extend GameObject but not Layer)
        this.hudCamera.ignore(this.floor as unknown as Phaser.GameObjects.GameObject);
        this.hudCamera.ignore(this.furnitures as unknown as Phaser.GameObjects.GameObject);
        this.hudCamera.ignore([
            this.objectsLayer,
            this.restingIngredientsLayer,
            this.cookLayer,
            this.stationOverlayLayer,
        ]);
    }

    private createHUD() {
        this.ordersHUD = new OrdersHUD(this);
        // Hint text sits just above the bottom of the HUD band
        this.hintText = this.add.text(CANVAS_WIDTH / 2, HUD_HEIGHT - 4, '', {fontSize: '12px', color: '#aaf'}).setOrigin(0.5, 1).setDepth(100);
        this.waitingOverlay = this.add.container(0, 0).setDepth(200);

        // Main camera does not render HUD elements — hudCamera handles them
        this.cameras.main.ignore([this.ordersHUD.getContainer(), this.hintText, this.waitingOverlay]);
    }

    private updateWaitingOverlay(): void {
        if (!this.synchroniser?.room?.state) return;
        const state = this.synchroniser.room.state;

        const shouldShow = !state.roundActive && !this._postRoundOverlay;
        const playerCount = state.cooks.size;

        if (!shouldShow) {
            if (this.waitingOverlay.length > 0) {
                this.waitingOverlay.removeAll(true);
                this._waitingOverlayPlayerCount = -1;
            }
            return;
        }

        // Only rebuild when player count changes (new player joins/leaves)
        if (this._waitingOverlayPlayerCount === playerCount) return;
        this._waitingOverlayPlayerCount = playerCount;

        this.waitingOverlay.removeAll(true);

        const bg = this.add.rectangle(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.7);
        this.waitingOverlay.add(bg);

        const title = this.add.text(CANVAS_WIDTH / 2, 60, 'Waiting for players...', {
            fontSize: '20px', color: '#ffffff', fontStyle: 'bold'
        }).setOrigin(0.5);
        this.waitingOverlay.add(title);

        const playerListY = 110;
        const lineHeight = 22;
        let idx = 0;
        state.cooks.forEach((cook) => {
            const isCreator = cook.id === state.creatorSessionId;
            const label = isCreator ? `${cook.playerName} (${cook.skinName}) [host]` : `${cook.playerName} (${cook.skinName})`;
            const color = isCreator ? '#ffdd55' : '#ccccff';
            const playerText = this.add.text(CANVAS_WIDTH / 2, playerListY + idx * lineHeight, label, {
                fontSize: '14px', color
            }).setOrigin(0.5);
            this.waitingOverlay.add(playerText);
            idx++;
        });

        const isCreator = this.sessionId === state.creatorSessionId;
        const promptY = playerListY + idx * lineHeight + 24;

        if (isCreator) {
            const btnWidth = 180;
            const btnHeight = 40;
            const btnBg = this.add.rectangle(CANVAS_WIDTH / 2, promptY, btnWidth, btnHeight, 0x228833)
                .setInteractive({ useHandCursor: true });
            const btnText = this.add.text(CANVAS_WIDTH / 2, promptY, 'Start Game', {
                fontSize: '16px', color: '#ffffff', fontStyle: 'bold'
            }).setOrigin(0.5);
            btnBg.on('pointerover', () => btnBg.setFillStyle(0x33aa44));
            btnBg.on('pointerout', () => btnBg.setFillStyle(0x228833));
            btnBg.on('pointerdown', () => this.synchroniser.messenger.sendStartRound());
            this.waitingOverlay.add([btnBg, btnText]);
        } else {
            const prompt = this.add.text(CANVAS_WIDTH / 2, promptY, 'Waiting for host to start...', {
                fontSize: '16px', color: '#888888', fontStyle: 'bold'
            }).setOrigin(0.5);
            this.waitingOverlay.add(prompt);
        }
    }

    setSessionId(sessionId: string) {
        this.sessionId = sessionId
    }

    public colyseusClientFactory(): Joinable {
        return new Client(SERVER_ENDPOINT);
    }

    update() {
        this.updateServerAboutUs();
        this.interpolateCooksToServer();
        this.updateHUD();
    }

    private updateHUD() {
        if (!this.synchroniser?.room?.state) return;
        const state = this.synchroniser.room.state;
        this.ordersHUD.update(state);
        if (state.roundActive) {
            if (this._postRoundOverlay) {
                this.hidePostRoundOverlay();
            }
        } else {
            if (this._wasRoundActive && !this._postRoundOverlay) {
                this.showPostRoundOverlay(state.score);
            }
        }
        this._wasRoundActive = state.roundActive;
        this.hintText.setText(this.getContextualHint(state));
        this.updateWaitingOverlay();
    }

    private showPostRoundOverlay(finalScore: number) {
        const cx = CANVAS_WIDTH / 2;
        const cy = CANVAS_HEIGHT / 2;

        const bg = this.add.rectangle(cx, cy, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.7)
            .setDepth(200);

        const title = this.add.text(cx, cy - 60, 'Round Over!', {
            fontSize: '32px', color: '#ffffff', fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(201);

        const scoreLabel = this.add.text(cx, cy - 10, `Final Score: ${finalScore}`, {
            fontSize: '20px', color: '#ffff00'
        }).setOrigin(0.5).setDepth(201);

        const startBtnBg = this.add.rectangle(cx, cy + 30, 200, 36, 0x228833)
            .setDepth(201)
            .setInteractive({ useHandCursor: true });
        const startBtnText = this.add.text(cx, cy + 30, 'New Round', {
            fontSize: '14px', color: '#ffffff', fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(202);
        startBtnBg.on('pointerover', () => startBtnBg.setFillStyle(0x33aa44));
        startBtnBg.on('pointerout', () => startBtnBg.setFillStyle(0x228833));
        startBtnBg.on('pointerdown', () => this.synchroniser.messenger.sendStartRound());

        const lobbyBtnBg = this.add.rectangle(cx, cy + 80, 200, 40, 0x336699)
            .setDepth(201)
            .setInteractive({ useHandCursor: true });

        const lobbyBtnText = this.add.text(cx, cy + 80, 'Back to Lobby', {
            fontSize: '16px', color: '#ffffff'
        }).setOrigin(0.5).setDepth(202);

        lobbyBtnBg.on('pointerover', () => lobbyBtnBg.setFillStyle(0x4488bb));
        lobbyBtnBg.on('pointerout', () => lobbyBtnBg.setFillStyle(0x336699));
        lobbyBtnBg.on('pointerdown', () => this.returnToLobby());

        this._postRoundOverlay = this.add.container(0, 0, [
            bg, title, scoreLabel, startBtnBg, startBtnText, lobbyBtnBg, lobbyBtnText
        ]).setDepth(200);

        // Post-round overlay renders on hudCamera (full-canvas), not main camera.
        // Container.add() removes children from scene display list, so ignoring the container is sufficient.
        this.cameras.main.ignore(this._postRoundOverlay);
    }

    private hidePostRoundOverlay() {
        if (this._postRoundOverlay) {
            this._postRoundOverlay.destroy();
            this._postRoundOverlay = null;
        }
    }

    private returnToLobby() {
        this.synchroniser.room?.leave();
        this.scene.start('LobbyScene');
    }

    private getContextualHint(state: any): string {
        const cook = state.cooks.get(this.sessionId);

        if (!state.roundActive) {
            if (cook?.carries_id) {
                const ingredient = state.ingredients.get(cook.carries_id);
                if (ingredient && DISH_TYPES.has(ingredient.type as IngredientTypes)) {
                    return this._touchInput ? 'Tap to serve (free play)' : '[SPACE] Serve dish (free play)';
                }
            }
            return '';
        }

        if (!cook) return '';

        if (cook.carries_id) {
            const ingredient = state.ingredients.get(cook.carries_id);
            if (!ingredient) return '[SPACE] Drop ingredient';
            const type = ingredient.type as IngredientTypes;

            // Check if a colleague is in reach and can receive the ingredient
            for (const colleague of state.cooks.values()) {
                if (colleague.id === this.sessionId) continue;
                if (!colleague.carries_id && withinReach(colleague, cook)) {
                    return '[SPACE] Pass to colleague';
                }
            }

            // Check if any station can process this ingredient
            for (const station of state.stations.values()) {
                if (!station.ingredient) {
                    if (station.stationType === StationType.COUNTER) {
                        continue; // counters are passive storage, skip for recipe hints
                    }
                    const output = stationOutputType(station.stationType as StationType, type);
                    if (output) {
                        const stationName = station.stationType === StationType.CUTTING_BOARD ? 'cutting board' : 'stove';
                        return `[SPACE] Place on ${stationName} to make ${output.replace('_', ' ')}`;
                    }
                }
            }

            // Check if near a trash station
            for (const station of state.stations.values()) {
                if (station.stationType === StationType.TRASH && withinReach(station, cook)) {
                    return '[SPACE] Discard ingredient (trash)';
                }
            }

            // Check if a nearby empty counter is available for storage
            for (const station of state.stations.values()) {
                if (station.stationType === StationType.COUNTER && !station.ingredient) {
                    return '[SPACE] Place on counter';
                }
            }

            // Check if carrying a finished dish that matches an order
            for (const order of state.orders.values()) {
                if (order.dishType === type) {
                    return '[SPACE] Serve at window!';
                }
            }

            return '[SPACE] Drop ingredient';
        }

        // Not carrying: check if nearby cutting board needs chopping
        for (const station of state.stations.values()) {
            if (station.stationType === StationType.CUTTING_BOARD && station.ingredientId && !station.activeChopperId) {
                return '[Hold SPACE] Chop ingredient';
            }
        }

        // Check if nearby stations have finished or stored ingredients
        for (const station of state.stations.values()) {
            if (station.ingredientId && !station.isProcessing) {
                if (station.stationType === StationType.COUNTER) {
                    return '[SPACE] Pick up from counter';
                }
                return '[SPACE] Pick up processed ingredient';
            }
        }

        return '[SPACE] Pick up ingredient from crate';
    }

    public isChoppableStation(stationId: string): boolean {
        if (!this.synchroniser?.room?.state) return false;
        const state = this.synchroniser.room.state;
        const station = state.stations.get(stationId);
        if (!station || station.stationType !== StationType.CUTTING_BOARD) return false;
        if (!station.ingredientId || station.activeChopperId) return false;
        const ingredient = state.ingredients.get(station.ingredientId);
        if (!ingredient) return false;
        return !!stationOutputType(station.stationType as StationType, ingredient.type as IngredientTypes);
    }

    private findChoppableStation(): string | null {
        if (!this.synchroniser?.room?.state) return null;
        const state = this.synchroniser.room.state;
        const cook = state.cooks.get(this.sessionId);
        if (!cook || cook.carries_id) return null; // must be empty-handed

        for (const [id, station] of state.stations.entries()) {
            if (station.stationType !== StationType.CUTTING_BOARD) continue;
            if (!station.ingredientId) continue;
            if (station.activeChopperId) continue; // someone already chopping
            const ingredient = state.ingredients.get(station.ingredientId);
            if (!ingredient || !stationOutputType(station.stationType as StationType, ingredient.type as IngredientTypes)) continue;
            // Simple proximity check (client-side heuristic; server validates)
            const dx = cook.x - station.x;
            const dy = cook.y - station.y;
            if (Math.sqrt(dx * dx + dy * dy) <= 48) {
                return id;
            }
        }
        return null;
    }

    private interpolateCooksToServer() {
        if (!this.cookObjects) return;
        for (const cookSprite of this.cookObjects.getChildren()) {
            if (cookSprite instanceof CookSprite) {
                cookSprite.interpolateToServerPosition();
            }
        }
    }

    private snapAllCooksToServer() {
        if (!this.cookObjects) return;
        for (const cookSprite of this.cookObjects.getChildren()) {
            if (cookSprite instanceof CookSprite) {
                cookSprite.snapToServerPosition();
            }
        }
    }

    createDisplayLayers() {
        let map = this.make.tilemap({key: KitchenAssetNames.kitchen_map});
        let tileset = map.addTilesetImage(KitchenAssetNames.kitchen_floor);
        this.floor = map.createLayer('Floor layer', tileset, 0, 0);
        this.furnitures = map.createLayer('Furnitures layer', tileset, 0, 0);
        this.furnitures.setCollisionByExclusion([-1])
        this.objectsLayer = this.add.layer()
        this.createServingWindowMarkers(map);
        this.stationOverlayLayer = this.add.layer()
        this.restingIngredientsLayer = this.add.layer()
        this.cookLayer = this.add.layer()
        let instanceModel: any = Cook;
        let spriteModel: any = CookSprite;
        this.cookObjects = this.add.existing(new GameSpriteGroup(this, instanceModel, spriteModel))
        this.ingredientObjects = this.add.existing(new GameSpriteGroup(this, Ingredient, IngredientSprite))
        this.basketObjects = this.add.existing(new GameSpriteGroup(this, Basket, BasketSprite))
        this.stationObjects = this.add.existing(new GameSpriteGroup(this, Station, StationSprite))
    }

    private createServingWindowMarkers(map: Phaser.Tilemaps.Tilemap) {
        const SERVING_WINDOW_TILE = 3;
        const TILE_SIZE = 32;
        const actionLayer = map.layers[2]; // action layer index
        if (!actionLayer) return;

        const data = actionLayer.data;
        for (let row = 0; row < data.length; row++) {
            for (let col = 0; col < data[row].length; col++) {
                if (data[row][col].index === SERVING_WINDOW_TILE) {
                    const x = col * TILE_SIZE + TILE_SIZE / 2;
                    const y = row * TILE_SIZE + TILE_SIZE / 2;
                    const marker = this.add.rectangle(x, y, TILE_SIZE, TILE_SIZE, 0x00AAFF, 0.5);
                    const label = this.add.text(x, y, 'SERVE', {fontSize: '8px', color: '#fff'}).setOrigin(0.5);
                    this.objectsLayer.add(marker);
                    this.objectsLayer.add(label);
                }
            }
        }
    }




    //// Scene Objects management

    onObjectAdded(sprite: GameObject & IGameSprite<any>): void {
        if (sprite instanceof CookSprite) {
            logger.info(`Enter ${sprite.instance.id} as ${sprite.instance.skinName}`);

            this.cookLayer.add(sprite)
            if (sprite.isLocal) {
                this.initLocalPlayer(sprite);
            }
            // Keep local cook on top when any new cook joins
            if (this.localCookSprite) {
                this.cookLayer.bringToTop(this.localCookSprite);
            }
        } else if (sprite instanceof IngredientSprite) {
            this.restingIngredientsLayer.add(sprite)
        } else if (sprite instanceof BasketSprite) {
            this.objectsLayer.add(sprite)
            logger.info(`basket sprite created for ${sprite.instance.id}`)
        } else if (sprite instanceof StationSprite) {
            this.stationOverlayLayer.add(sprite)
        }
    }

    initLocalPlayer(sprite: CookSprite) {
        positionLocalCook(sprite)
        // Local player renders on top of remote cooks for visual clarity when overlapping
        sprite.setDepth(1)
        this.localCookSprite = sprite
    }

    onObjectRemove(sprite: GameObject & IGameSprite<any>) {
        if (sprite instanceof CookSprite) {
            logger.info(`${sprite.instance.id} has left the kitchen`)
        }
    }

    //// Inputs

    prepare_input() {
        this.cursors = this.input.keyboard.createCursorKeys();
        this.input.addPointer(1)
        this.floor.setInteractive() // should floor be source of mouse input?
        this.cursors.space.on(DOWN, () => {
            const choppableStation = this.findChoppableStation();
            if (choppableStation) {
                this.synchroniser.messenger.sendChopStart(choppableStation);
                this.isChopping = true;
            } else {
                this.synchroniser.messenger.sendPickOrDrop();
            }
        })
        this.cursors.space.on(UP, () => {
            if (this.isChopping) {
                this.synchroniser.messenger.sendChopStop();
                this.isChopping = false;
            }
        })
        const enterKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
        enterKey.on(DOWN, () => {
            this.synchroniser.messenger.sendStartRound();
        });
        const tabKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.TAB);
        tabKey.on(DOWN, () => {
            this.toggleRecipeBook();
        });

        if (isTouchDevice()) {
            this._touchInput = new TouchInputHandler(this, {
                onPick: () => this.synchroniser.messenger.sendPickOrDrop(),
                onChopStart: (stationId) => {
                    this.synchroniser.messenger.sendChopStart(stationId);
                    this.isChopping = true;
                },
                onChopStop: () => {
                    this.synchroniser.messenger.sendChopStop();
                    this.isChopping = false;
                },
                onStartRound: () => this.synchroniser.messenger.sendStartRound(),
                onFace: (cmd) => this.synchroniser.messenger.send_move_command(cmd),
            }, () => this.localCookSprite);

            // Virtual joystick + action button rendered in the controls zone below the game
            this._mobileControls = new MobileControls(this, {
                onPick: () => this.synchroniser.messenger.sendPickOrDrop(),
                onChopStart: () => {
                    const stationId = this.findChoppableStation();
                    if (stationId) {
                        this.synchroniser.messenger.sendChopStart(stationId);
                        this.isChopping = true;
                    }
                },
                onChopStop: () => {
                    this.synchroniser.messenger.sendChopStop();
                    this.isChopping = false;
                },
            });

            // Main camera must not render the control overlay GameObjects
            // (they live at HUD depth via setScrollFactor(0) — ignore them from main)
        }
    }

    private analyseInputDirection(pointer: Phaser.Input.Pointer): MoveCommand {
        return mouseDirectionFromSprite(pointer, this.cursors, this.localCookSprite);
    }

    private lastSentCommand: MoveCommand | null = null

    sendCommandMoveOnUserInput() {
        if (!this.synchroniser.connected()) return false

        // On touch devices use the virtual joystick command instead of pointer-position movement.
        // This prevents the pointer-based logic from fighting the joystick when the player
        // taps anywhere on the game canvas (pointer position is meaningless for movement on touch).
        let cmd: MoveCommand;
        if (this._mobileControls) {
            cmd = this._mobileControls.getJoystickCommand();
        } else {
            let pointer = this.input.activePointer;
            cmd = this.analyseInputDirection(pointer);
        }

        if (!this.lastSentCommand || cmd.left !== this.lastSentCommand.left ||
            cmd.right !== this.lastSentCommand.right || cmd.up !== this.lastSentCommand.up ||
            cmd.down !== this.lastSentCommand.down) {
            this.synchroniser.messenger.send_move_command(cmd)
            this.lastSentCommand = cmd;
        }
    }

    ///// Client -> Server changes
    private updateServerAboutUs() {
        if (this.localCookSprite == undefined)
            return
        if (this._waitingOverlayPlayerCount >= 0) return; // overlay shown, don't move
        this.sendCommandMoveOnUserInput();
        // Position is now authoritative from server; no longer uploading local position
    }

    //// Recipe book

    private toggleRecipeBook() {
        this.recipeBookVisible = !this.recipeBookVisible;
        if (this.recipeBookVisible) {
            this.recipeBookPanel = this.buildRecipeBook();
            this.add.existing(this.recipeBookPanel);
            // Recipe book renders on hudCamera only
            this.cameras.main.ignore(this.recipeBookPanel);
        } else {
            this.recipeBookPanel?.destroy();
            this.recipeBookPanel = null;
        }
    }

    private buildRecipeBook(): Phaser.GameObjects.Container {
        const PANEL_X = 20;
        const PANEL_Y = 50;
        const PANEL_W = 600;
        const PANEL_H = 390;
        const PAD = 12;
        const LINE_H = 18;

        const container = this.add.container(0, 0);
        container.setScrollFactor(0);
        container.setDepth(200);

        // Background
        const bg = this.add.rectangle(PANEL_X + PANEL_W / 2, PANEL_Y + PANEL_H / 2, PANEL_W, PANEL_H, 0x111122, 0.92);
        container.add(bg);

        // Title
        const title = this.add.text(PANEL_X + PAD, PANEL_Y + PAD, 'Recipe Book  [TAB to close]', {
            fontSize: '15px', color: '#ffee88', fontStyle: 'bold'
        }).setScrollFactor(0);
        container.add(title);

        let y = PANEL_Y + PAD + LINE_H + 6;

        for (const recipe of RECIPES) {
            // Recipe header
            const header = this.add.text(PANEL_X + PAD, y, `${recipe.displayName}`, {
                fontSize: '13px', color: '#aaddff', fontStyle: 'bold'
            }).setScrollFactor(0);
            container.add(header);
            y += LINE_H;

            // Build step chain string
            const chain = this.buildRecipeChain(recipe);
            const stepText = this.add.text(PANEL_X + PAD + 8, y, chain, {
                fontSize: '11px', color: '#cccccc', wordWrap: {width: PANEL_W - PAD * 2 - 8}
            }).setScrollFactor(0);
            container.add(stepText);
            y += stepText.height + 6;
        }

        return container;
    }

    private buildRecipeChain(recipe: import("../model/recipe_registry").Recipe): string {
        const parts: string[] = [];
        const seen = new Set<string>();
        for (const step of recipe.steps) {
            if (step.action === 'chop') {
                const raw = step.inputs[0].replace(/_/g, ' ');
                const out = step.output.replace(/_/g, ' ');
                const key = `${step.inputs[0]}->${step.output}`;
                if (!seen.has(key)) {
                    parts.push(`${raw} -[chop]-> ${out}`);
                    seen.add(key);
                }
            } else if (step.action === 'cook') {
                const inp = step.inputs[0].replace(/_/g, ' ');
                const out = step.output.replace(/_/g, ' ');
                parts.push(`${inp} -[stove]-> ${out}`);
            } else if (step.action === 'merge') {
                const ins = step.inputs.map(i => i.replace(/_/g, ' ')).join(' + ');
                const out = step.output.replace(/_/g, ' ');
                parts.push(`${ins} -[combine]-> ${out}`);
            }
        }
        return parts.join('  |  ');
    }
}

export function positionLocalCook(cookSprite: CookSprite): Position {
    let centerX = MAP_WIDTH / 2;
    let centerY = MAP_HEIGHT / 2;
    cookSprite.setPosition(centerX, centerY)
    logger.info(`sprite of ${cookSprite.instance.printableId} positioned at (${centerX},${centerY})`)
    return {x: centerX, y: centerY}
}
